import { TypedBroadcastChannel } from '#browser/broadcast';
import { parseJSON } from '#json/parse';
import { isDefined } from '#misc';

import type { LeaderChannelMessage } from './types/leader-channel-message.type';
import type { LeaderElectorOptions } from './types/leader-elector.options';
import type { LeaderState } from './types/leader-state.type';

/**
 * Coordinates leader election across browser tabs using a storage-backed lease and a
 * dedicated {@link BroadcastChannel}.
 *
 * Only one instance across all tabs sharing the same `leaderKey` and `channelName` will
 * hold the leader role at a time. The leader is determined by writing a TTL lease to
 * storage; the lease is renewed via periodic heartbeats broadcast to all other tabs.
 *
 * When the current leader tab is closed or calls {@link LeaderElector.destroy}, it
 * broadcasts a resign message so remaining tabs can race to acquire leadership immediately
 * rather than waiting for the lease to expire.
 *
 * @example
 * ```ts
 * const elector = new LeaderElector({
 *   channelName: 'my-app:leader',
 *   leaderKey:   '__my-app-leader__',
 *   onBecomeLeader: () => { startPolling(); },
 *   onLeaderResign: () => { stopPolling(); },
 * });
 *
 * // When the tab is closing or the elector is no longer needed:
 * elector.destroy();
 * ```
 */
export class LeaderElector {
  private readonly channel: TypedBroadcastChannel<LeaderChannelMessage>;

  private destroyed = false;

  private electionTimer?: number;

  private readonly heartbeatIntervalMs: number;

  private heartbeatTimer?: number;

  private readonly instanceId = crypto.randomUUID();

  private readonly jitterMaxMs: number;

  private lastLeaderHeartbeatAt?: number;

  private leader = false;

  private readonly leaderKey: string;

  private readonly leaseDurationMs: number;

  private readonly onBecomeLeader: () => void;

  private readonly onLeaderResign?: () => void;

  private readonly storage: Storage;

  /**
   * Creates a new leader elector and immediately begins the election process.
   *
   * @param options - Configuration for this elector instance. See {@link LeaderElectorOptions}.
   */
  constructor(options: LeaderElectorOptions) {
    const {
      channelName,
      heartbeatIntervalMs,
      jitterMaxMs,
      leaderKey,
      leaseDurationMs,
      onBecomeLeader,
      onLeaderResign,
      storage,
    } = options;

    this.heartbeatIntervalMs = heartbeatIntervalMs ?? 2000;
    this.jitterMaxMs = jitterMaxMs ?? 200;
    this.leaseDurationMs = leaseDurationMs ?? 5000;
    this.leaderKey = leaderKey;
    this.onBecomeLeader = onBecomeLeader;
    this.onLeaderResign = onLeaderResign;
    this.storage = storage ?? window.localStorage;

    this.channel = new TypedBroadcastChannel<LeaderChannelMessage>(channelName, (message) => {
      this.handleMessage(message);
    });

    this.startElection();
  }

  /**
   * Whether this instance currently holds the leader role.
   */
  get isLeader(): boolean {
    return this.leader;
  }

  /**
   * Destroys the elector, cleans up all timers, and closes the channel.
   *
   * If this instance is the current leader, it removes its lease from storage and
   * broadcasts a resign message so other tabs can immediately race for leadership
   * rather than waiting for the lease to expire naturally.
   */
  destroy(): void {
    this.destroyed = true;

    if (isDefined(this.electionTimer)) {
      clearInterval(this.electionTimer);
    }

    this.stopHeartbeat();

    if (this.leader) {
      this.storage.removeItem(this.leaderKey);

      this.channel.post({
        id: this.instanceId,
        type: 'leader-resign',
      });
    }

    this.channel.close();
  }

  /**
   * Routes incoming channel messages to the appropriate handler.
   * Ignores messages originating from this instance (defensive guard —
   * the native BroadcastChannel API does not deliver own-posted messages).
   */
  private handleMessage(message: LeaderChannelMessage) {
    if (message.id === this.instanceId) {
      return;
    }

    switch (message.type) {
      case 'leader-alive': {
        this.lastLeaderHeartbeatAt = Date.now();
        this.yieldLeadership();
        break;
      }

      case 'leader-resign': {
        this.tryAcquireWithJitter();
        break;
      }
    }
  }

  /**
   * Begins the election process: attempts immediate acquisition, then sets up a
   * recurring interval to re-evaluate leadership when the current leader's lease
   * appears stale.
   */
  private startElection() {
    this.tryAcquire();

    this.electionTimer = window.setInterval(() => {
      if (this.destroyed) {
        return;
      }

      if (!this.leader) {
        if (isDefined(this.lastLeaderHeartbeatAt)) {
          const elapsed = Date.now() - this.lastLeaderHeartbeatAt;

          if (elapsed < this.leaseDurationMs) {
            return;
          }
        }
      }

      this.tryAcquireWithJitter();
    }, this.heartbeatIntervalMs);
  }

  /**
   * Starts the leader heartbeat interval if it is not already running.
   * Each tick renews the lease in storage and broadcasts a `leader-alive` message
   * so followers know a healthy leader exists.
   */
  private startHeartbeat() {
    if (this.destroyed) {
      return;
    }

    if (isDefined(this.heartbeatTimer)) {
      return;
    }

    this.heartbeatTimer = window.setInterval(() => {
      if (this.destroyed || !this.leader) {
        return;
      }

      const newState: LeaderState = {
        expiresAt: Date.now() + this.leaseDurationMs,
        id: this.instanceId,
      };

      this.storage.setItem(this.leaderKey, JSON.stringify(newState));

      this.channel.post({
        id: this.instanceId,
        type: 'leader-alive',
      });
    }, this.heartbeatIntervalMs);
  }

  /**
   * Clears the leader heartbeat interval timer.
   */
  private stopHeartbeat() {
    if (isDefined(this.heartbeatTimer)) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  /**
   * Attempts to acquire leadership by reading the current lease from storage.
   *
   * If no valid lease exists (absent, expired, or owned by this instance), this
   * instance writes a new lease, marks itself as leader, and starts the heartbeat.
   * `onBecomeLeader` is called only on the first acquisition — not on lease renewals.
   */
  private tryAcquire() {
    if (this.destroyed) {
      return;
    }

    const raw = this.storage.getItem(this.leaderKey);
    const now = Date.now();

    if (isDefined(raw)) {
      const parsed = parseJSON<LeaderState>(raw);

      if (!parsed.isFailure()) {
        const state = parsed.unwrap();

        if (state.id !== this.instanceId && state.expiresAt > now) {
          this.leader = false;

          return;
        }
      }
    }

    const acquiredNow = !this.leader;
    const newState: LeaderState = {
      expiresAt: now + this.leaseDurationMs,
      id: this.instanceId,
    };

    this.storage.setItem(this.leaderKey, JSON.stringify(newState));
    this.leader = true;

    if (acquiredNow) {
      this.startHeartbeat();
      this.onBecomeLeader();
    }
  }

  /**
   * Schedules a leadership acquisition attempt after a random jitter delay.
   *
   * Used in contention scenarios — when a leader resigns (all tabs are notified
   * simultaneously) or when the election interval detects a stale lease — to
   * reduce the probability of multiple tabs writing themselves as leader in the same tick.
   */
  private tryAcquireWithJitter() {
    if (this.destroyed) {
      return;
    }

    const jitter = Math.random() * this.jitterMaxMs;

    window.setTimeout(() => {
      this.tryAcquire();
    }, jitter);
  }

  /**
   * Yields leadership to the newly detected leader.
   * Stops the heartbeat and fires `onLeaderResign` if this instance was the leader.
   * Has no effect if this instance is already a follower.
   */
  private yieldLeadership() {
    if (!this.leader) {
      return;
    }

    this.leader = false;
    this.stopHeartbeat();
    this.onLeaderResign?.();
  }
}
